import { useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  FirestoreService,
  listFilesInFolder,
} from "../../../api/firebase/firestoreService";
import { FirestoreCollectionReference } from "../../../api/firebase/references";
import { getNccnGuideLines } from "../../../api/postCall/nccnGuidelines";
import {
  ChecklistData,
  ChecklistQuestion,
} from "../../../data-model/medicalNecessityReview";
import {
  setChecklistData,
  setEmrDocumentUrl,
  setEmrDocumentUrls,
  setError,
  setGuidelineItems,
  setLoading,
  setUserAnswers,
} from "../../../redux/slice/medicalNecessityReviewSlice";
import { RootState } from "../../../redux/store/store";
import { injectStatements } from "../utils/statementInjector";

// Helper functions to transform Firestore question_tree format to expected format
function transformQuestionTree(questionTree: any[]): ChecklistQuestion[] {
  return questionTree.map((q) => transformQuestion(q));
}

function transformQuestion(q: any): ChecklistQuestion {
  return {
    id: q.id,
    question: q.question,
    question_type: q.question_type || "single_correct", // Default if missing
    options: (q.options || []).map((opt: any) => ({
      label: opt.text || opt.label,
      value: opt.id || opt.value || opt.text, // Use opt.id first to match answer IDs
    })),
    children: q.children ? transformChildren(q.children) : undefined,
    facts: q.facts,
    selected_answer: q.answer?.id || q.selected_answer,
    required: q.required,
  };
}

function transformChildren(
  children: Record<string, any>,
): Record<string, ChecklistQuestion[]> {
  const transformed: Record<string, ChecklistQuestion[]> = {};
  for (const [key, value] of Object.entries(children)) {
    if (Array.isArray(value)) {
      transformed[key] = value.map(transformQuestion);
    } else if (value && typeof value === "object") {
      // If single object, wrap in array
      transformed[key] = [transformQuestion(value)];
    }
  }
  return transformed;
}

// Extract answers from the question tree to initialize user answers
function extractAnswersFromTree(questionTree: any[]): Record<string, any> {
  const answers: Record<string, any> = {};

  function extractFromQuestion(q: any) {
    if (q.answer && q.answer.id) {
      // Store the answer ID for this question
      answers[q.id] = q.answer.id;

      // If this question has children, traverse the child that matches the answer
      if (q.children && q.children[q.answer.id]) {
        const childQuestion = q.children[q.answer.id];
        if (Array.isArray(childQuestion)) {
          childQuestion.forEach(extractFromQuestion);
        } else if (childQuestion && typeof childQuestion === "object") {
          extractFromQuestion(childQuestion);
        }
      }
    }
  }

  questionTree.forEach(extractFromQuestion);
  return answers;
}

export function useMedicalNecessityData(orderId: string) {
  const dispatch = useDispatch();
  const { checklistData, emrDocumentUrl, guidelineItems, loading, error } =
    useSelector((state: RootState) => state.medicalNecessityReview);

  useEffect(() => {
    if (!orderId) return;

    const fetchData = async () => {
      dispatch(setLoading(true));

      try {
        // 1. Fetch checklist from Firestore
        const checklistPath =
          FirestoreCollectionReference.medicalNecessityChecklistData(orderId);
        console.log("🔍 Fetching checklist from path:", checklistPath);
        const checklistDoc =
          await FirestoreService.getDocument<ChecklistData>(checklistPath);

        console.log("📋 Checklist data received:", checklistDoc);

        // 2. Transform question_tree (new format) or use questions (old format)
        const rawDoc = checklistDoc as any;
        let questions: ChecklistQuestion[] | undefined;

        if (rawDoc?.question_tree && Array.isArray(rawDoc.question_tree)) {
          console.log("🔄 Transforming question_tree to questions format");
          questions = transformQuestionTree(rawDoc.question_tree);

          // Extract answers from the tree and set them in Redux
          const extractedAnswers = extractAnswersFromTree(rawDoc.question_tree);
          console.log("📝 Extracted answers from tree:", extractedAnswers);
          dispatch(setUserAnswers(extractedAnswers));
        } else if (checklistDoc?.questions) {
          console.log("✓ Using existing questions format");
          questions = checklistDoc.questions;
        }

        if (questions && questions.length > 0) {
          // 3. Inject statements based on order ID
          const checklistWithStatements: ChecklistData = {
            questions: injectStatements(questions, orderId),
            metadata: rawDoc.metadata || {
              patient_id: rawDoc.patient_id,
              order_id: orderId,
              insurance_type: rawDoc.insurance_type,
              recommendation: rawDoc.recommendation,
              recommendation_text: rawDoc.recommendation_text,
            },
          };

          console.log("✅ Checklist with statements:", checklistWithStatements);
          dispatch(setChecklistData(checklistWithStatements));
        } else {
          console.warn("⚠️ No checklist questions found in document");
          console.warn("Document structure:", Object.keys(rawDoc || {}));
        }

        // 3. Fetch all clinical attachments from Firebase Storage folder
        const attachmentsPath = `onco_emr/${orderId}/clinical_attachment`;
        console.log(
          "📄 Fetching clinical attachments from folder:",
          attachmentsPath,
        );
        try {
          const attachmentUrls = await listFilesInFolder(attachmentsPath);
          console.log(
            `✅ Found ${attachmentUrls.length} clinical attachment(s):`,
            attachmentUrls,
          );
          if (attachmentUrls.length > 0) {
            dispatch(setEmrDocumentUrls(attachmentUrls));
            // Also set the first URL to the old field for backward compatibility
            dispatch(setEmrDocumentUrl(attachmentUrls[0]));
          } else {
            console.warn("⚠️ No clinical attachments found in folder");
          }
        } catch (emrError) {
          console.warn(
            "⚠️ Clinical attachments folder not accessible:",
            emrError,
          );
          // Don't fail the whole operation if attachments are missing
        }

        // 4. Fetch guidelines using NCCN API (same as CPT codes page)
        try {
          console.log(
            "📘 Fetching NCCN guidelines via API for order:",
            orderId,
          );

          const nccnResponse = await getNccnGuideLines({ order_id: orderId });

          if (nccnResponse?.data && nccnResponse.data.length > 0) {
            console.log(
              `✅ Received ${nccnResponse.data.length} guideline item(s) from API`,
              nccnResponse.data,
            );

            // Set the guideline items for the modal
            dispatch(setGuidelineItems(nccnResponse.data as any));
          } else {
            console.log("📘 No guidelines returned from NCCN API");
          }
        } catch (guidelineError) {
          console.warn(
            "⚠️ Could not fetch NCCN guidelines from API:",
            guidelineError,
          );
          // Don't fail the whole operation if guidelines are missing
        }

        // 5. Load previously saved answers if any
        try {
          const answersPath =
            FirestoreCollectionReference.medicalNecessityUserAnswers(orderId);
          const savedAnswersDoc = await FirestoreService.getDocument<{
            answers: Record<string, any>;
          }>(`${answersPath}/user_answers`);

          if (savedAnswersDoc?.answers) {
            dispatch(setUserAnswers(savedAnswersDoc.answers));
          }
        } catch (answersError) {
          console.warn("No previous answers found:", answersError);
          // This is ok - first time visiting the screen
        }
      } catch (error: any) {
        console.error("Error fetching medical necessity data:", error);
        dispatch(setError(error?.message || "Failed to load data"));
      } finally {
        dispatch(setLoading(false));
      }
    };

    fetchData();
  }, [orderId, dispatch]);

  return {
    checklistData,
    emrDocumentUrl,
    guidelineItems,
    loading,
    error,
  };
}
